import { Component, OnInit, OnDestroy, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { Subscription, switchMap, of, combineLatest } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

import { RecordingService } from '../../services/recording/recording.service';
import { WalletService } from '../../services/wallet/wallet.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { UserService } from '../../services/user/user.service';
import { SessionsService } from '../../services/sessions/sessions.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { TrialService } from '../../services/trial/trial.service';
import { MuteService } from '../../services/mute/mute.service';
import { VideoOffService } from '../../services/video-off/video-off.service';
import { ChatService } from '../../services/chat/chat.service';
import { KeybindService } from '../../services/keybinds/keybinds.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Locations, Prompt, PromptTypes } from '@sc/types';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { StudioSidebarItem } from '@sc/types';
import { SquadshotService } from '../../services/squadshot/squadshot.service';
import { ToursService } from '../../services/tours/tours.service';
import { RoleLimits, Roles } from '@sc/types';
import { RolesService } from '../../services/roles/roles.service';
import { WebhookEvent, WebhookEventNames } from '@sc/types';
import { CalifoneService } from '../../services/califone/califone.service';
import { PromptsService } from '../../services/prompts/prompts.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-studio-controls',
  templateUrl: './studio-controls.component.html',
  styleUrls: ['./studio-controls.component.scss'],
})
export class StudioControlsComponent implements OnInit, OnDestroy, AfterViewInit {
  activeUser$ = this.userService.activeUser$;
  studioSession$ = this.sessionsService.studioSession$;
  prompts$ = this.sessionsService.sessionPrompts$;
  dolbyScreenshare$ = this.dolbyService.screenshare$;
  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyScreenshare$ = this.dailyService.screenshare$;
  dailyCall$ = this.dailyService.dailyCall$;

  role$ = this.rolesService.role$;
  roles = Roles;
  roleLimits = RoleLimits;

  trialStatus$ = this.trialService.trialStatus$;
  recording$ = this.recordingService.recording$;
  availableRecordingTime: number;

  mute$ = this.muteService.mute$;
  videoOff$ = this.videoOffService.videoOff$;
  videoPermission = false;
  disableCameraToggle = false;
  unreadChatGroups$ = this.chatService.unreadChatGroups$;

  backstage = false;
  disableRecordingButton = false;
  isMobile = false;
  warned = new Set<string>();
  recordButtonState = {
    disabled: false,
    message: 'Record Button',
  };

  sidebarItems: StudioSidebarItem[] = [];
  editingSidebars$ = this.studioSidebarService.editingSidebars$;
  constraints$ = this.responsiveService.constraints$;
  editTimeout: NodeJS.Timeout;
  lockTimeout: NodeJS.Timeout;

  subs: Array<Subscription> = [];

  constructor(
    private router: Router,
    private toastrService: ToastrService,
    private recordingService: RecordingService,
    private walletService: WalletService,
    private analyticsService: AnalyticsService,
    private userService: UserService,
    private sessionsService: SessionsService,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private equipmentService: EquipmentService,
    private trialService: TrialService,
    private muteService: MuteService,
    private videoOffService: VideoOffService,
    private califoneService: CalifoneService,
    private chatService: ChatService,
    private keybindService: KeybindService,
    private deviceDetectorService: DeviceDetectorService,
    private promptsService: PromptsService,
    private studioSidebarService: StudioSidebarService,
    private rolesService: RolesService,
    private toursService: ToursService,
    private responsiveService: ResponsiveService,
    private squadshotService: SquadshotService,
    private elRef: ElementRef
  ) {}

  /**
   * Sets up the component's initial state
   */
  ngOnInit() {
    this.setupKeybinds();
    this.setupWallet();
    this.setupUnreadNotification();
    this.setupLocation();
    this.setupPermission();
    this.setupSidebarItems();
    this.setupRole();
    this.isMobile = this.deviceDetectorService.isMobile();
    // Set navTime for analytics timeOnPage in studio vs in greenroom
    this.analyticsService.navTime = performance.now();
  }

  async ngAfterViewInit() {
    // Wait for the UI to load before starting the tour.
    setTimeout(() => {
      this.toursService.startTours();
    }, 0);
  }

  setForwarding(e) {
    this.dolbyService.setVideoForwarding(e.detail.value);
  }

  setupRole() {
    const sub = this.role$.subscribe((role) => {
      if (role >= Roles.SHOW_MANAGER) {
        this.setupManagerNotifications();
      }
      this.refreshRecordButtonState();
    });
    this.subs.push(sub);
  }

  setupLocation() {
    const sub = this.dolbyService.location$.subscribe((location) => {
      if (location === Locations.BACKSTAGE) this.backstage = true;
    });
    this.subs.push(sub);
    this.backstage = this.router.url.includes('/location/backstage') ? true : false;
  }

  setupPermission() {
    const sub = this.equipmentService.hasPermission$.subscribe((permission) => {
      this.videoPermission = permission[1];
    });

    this.subs.push(sub);

    const sub2 = this.equipmentService.constraints$.subscribe((constraints) => {
      this.disableCameraToggle = !constraints?.video;
    });

    this.subs.push(sub2);
  }

  setupSidebarItems() {
    let sidebarKeybindsSubs = [];
    const sub = combineLatest([this.studioSidebarService.sidebarItems$, this.role$]).subscribe(
      ([sidebarItemsValue, role]) => {
        if (role < this.roleLimits.RECORDING_READ) {
          this.sidebarItems = sidebarItemsValue.map((sidebar) =>
            sidebar?.appearance === 'recordings' ? null : sidebar
          );
        } else {
          this.sidebarItems = sidebarItemsValue;
        }

        // Sidebar Keybinds
        sidebarKeybindsSubs.forEach((sub) => sub.unsubscribe());
        sidebarKeybindsSubs = [];
        for (const [i, sidebar] of this.sidebarItems.entries()) {
          if (!sidebar) continue;
          const sub = this.keybindService
            .addShortcut({ keys: `control.${i + 1}` })
            .pipe(throttleTime(100, undefined, { leading: true, trailing: false }))
            .subscribe(() => {
              document.getElementById(`studio-controls-${sidebar}-button`).click();
            });
          sidebarKeybindsSubs.push(sub);
        }
      }
    );
    this.subs.push(sub);
  }

  /**
   * Keybinds for controls
   */
  setupKeybinds() {
    // Mute Keybind: cntl + d
    const muteSub = this.keybindService
      .addShortcut({ keys: 'control.d' })
      .pipe(throttleTime(100, undefined, { leading: true, trailing: false }))
      .subscribe(() => {
        this.toggleMute();
      });
    // Video Keybind: Cntl + q
    const camSub = this.keybindService
      .addShortcut({ keys: 'control.e' })
      .pipe(throttleTime(100, undefined, { leading: true, trailing: false }))
      .subscribe(() => {
        if (this.equipmentService.selectedCamera$.value) {
          this.toggleVideoOff();
        }
      });
    // Screenshare Keybind: Cntl + s
    const screenSub = this.keybindService
      .addShortcut({ keys: 'control.s' })
      .pipe(throttleTime(100, undefined, { leading: true, trailing: false }))
      .subscribe(() => {
        this.toggleScreenShare();
      });
    // Start/Stop Recording Keybind: Cntl + r
    const recordSub = this.keybindService
      .addShortcut({ keys: 'control.r' })
      .pipe(throttleTime(3000, undefined, { leading: true, trailing: false }))
      .subscribe(() => {
        this.toggleRecording();
      });
    // Start SquadShot: Cntl + g
    const squadshotSub = this.keybindService
      .addShortcut({ keys: 'control.g' })
      .pipe(throttleTime(100, undefined, { leading: true, trailing: false }))
      .subscribe(() => {
        this.takeSquadshot();
      });

    this.subs.push(muteSub, camSub, screenSub, recordSub, squadshotSub);
  }

  refreshRecordButtonState() {
    if (this.disableRecordingButton) {
      this.recordButtonState = {
        disabled: true,
        message: this.dailyService.waitingForCloudRecording$.value
          ? `Waiting for cloud recording to ${this.recording$.value ? 'start' : 'stop'}...`
          : 'Loading...',
      };
      return;
    }
    if (this.role$.value < this.roleLimits.RECORDING_CREATE) {
      this.recordButtonState = {
        disabled: true,
        message: 'Ask Show Manager to Start/Stop the Recording',
      };
      return;
    }
    if (
      this.availableRecordingTime <= 0 &&
      this.trialStatus$.value?.onTrial !== true &&
      this.walletService.studioPlan$.value?.recordingHours !== 'Unlimited'
    ) {
      this.recordButtonState = {
        disabled: true,
        message: 'You do not have enough available credits to record.',
      };
      return;
    }
    this.recordButtonState = {
      disabled: false,
      message: (this.recording$.value ? 'Stop' : 'Start') + ' Recording (Ctrl + R)',
    };
    return;
  }

  /**
   * Toggles the recording field in Firestore
   * Increments the take number
   * Sets the session ID for cloud recordings
   */
  async toggleRecording() {
    if (this.recordButtonState.disabled) return;
    this.disableRecordingButton = true;
    this.refreshRecordButtonState();
    const lastValue = this.recording$.value;
    if (this.recording$.value) {
      await this.recordingService.stopRecording();

      const recordingStoppedEvent: WebhookEvent = {
        name: WebhookEventNames.RECORDING_STOPPED,
        orgID: this.studioSession$.value.orgID,
        sessionID: this.studioSession$.value.sessionID,
        sessionTitle: this.studioSession$.value.sessionTitle,
        showID: this.studioSession$.value.showID,
        showName: this.studioSession$.value.showTitle,
        take: this.studioSession$.value.take,
      };
      this.califoneService.emitWebhookEvent(recordingStoppedEvent, this.studioSession$.value.orgID);

      this.studioSidebarService.toggleSidebar('recordings', true);
    } else {
      await this.recordingService.startRecording();

      const recordingStartedEvent: WebhookEvent = {
        name: WebhookEventNames.RECORDING_STARTED,
        orgID: this.studioSession$.value.orgID,
        sessionID: this.studioSession$.value.sessionID,
        sessionTitle: this.studioSession$.value.sessionTitle,
        showID: this.studioSession$.value.showID,
        showName: this.studioSession$.value.showTitle,
        take: this.studioSession$.value.take,
      };
      this.califoneService.emitWebhookEvent(recordingStartedEvent, this.studioSession$.value.orgID);
    }

    this.analyticsService.track('toggled recording', { enabled: this.recording$.value });
    await this.recording$.nextExistingValue((recording) => recording !== lastValue);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    this.refreshRecordButtonState();
    await this.dailyService.waitingForCloudRecording$.nextExistingValue((waiting) => waiting === false);
    this.disableRecordingButton = false;
    this.refreshRecordButtonState();
  }

  /**
   * Toggles the mute for Participant
   */
  async toggleMute() {
    this.muteService.toggleMute();
    this.analyticsService.track('toggled muted', { enabled: this.mute$.value });
  }

  /**
   * Toggles the video off for Participant
   */
  async toggleVideoOff() {
    this.videoOffService.toggleVideo();
    this.analyticsService.track('toggled video off', { enabled: !this.videoOff$.value });
  }

  /**
   * Toggles screenshare on and off
   */
  async toggleScreenShare() {
    if (this.dolbyConversation$.value) {
      // Dolby
      if (this.dolbyScreenshare$.value?.info?.externalId === this.activeUser$.value.uid) {
        await this.dolbyService.stopScreenShare();
        this.analyticsService.track('toggled share screen', { success: true });
      } else if (this.dolbyScreenshare$.value) {
        this.toastrService.warning(`Another user is sharing their screen`, `Cannot share your screen right now`, {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 5 * 1000,
          toastComponent: GeneralToastComponent,
        });
      } else {
        try {
          await this.dolbyService.startScreenShare();
          this.analyticsService.track('toggled share screen', { success: true });
        } catch (error) {
          console.warn(`⚠ ⚠  err.message ➡ ${JSON.stringify(error.message, null, 2)} ⚠ ⚠ `);
          this.analyticsService.track('toggled share screen', { success: false });
        }
      }
    } else if (this.dailyCall$.value) {
      // Daily
      if (this.dailyScreenshare$.value?.userData?.scUID === this.activeUser$.value.uid) {
        await this.dailyService.stopScreenShare();
        this.analyticsService.track('toggled share screen', { success: true });
      } else if (this.dailyScreenshare$.value) {
        this.toastrService.warning(`Another user is sharing their screen`, `Cannot share your screen right now`, {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 5 * 1000,
          toastComponent: GeneralToastComponent,
        });
      } else {
        await this.dailyService.startScreenShare();
        this.analyticsService.track('toggled share screen', { success: true });
      }
    }
  }

  /**
   * Sets up the available recording time
   */
  setupWallet() {
    const sub = this.walletService.studioWallet$.subscribe((wallet) => {
      if (!wallet) {
        this.availableRecordingTime = 0;
      } else {
        this.availableRecordingTime = this.walletService.convertTimeCodeToSeconds(wallet.available);
      }
      this.refreshRecordButtonState();
    });

    this.subs.push(sub);
  }

  setupManagerNotifications() {
    const sub = this.recording$
      .pipe(
        switchMap((isRecording) => {
          if (isRecording)
            return this.sessionsService.getAllRecordingsFromTake(
              this.studioSession$.value.sessionID,
              this.studioSession$.value.take
            );
          else return of(null);
        })
      )
      .subscribe((recordings) => {
        if (recordings) {
          const warnings = recordings.filter(
            (recording) => recording.warnings && !this.warned.has(recording.recordingID)
          );
          warnings.forEach((recording) => {
            this.warned.add(recording.recordingID);
            this.toastrService.warning(`Recording ${recording.fileName} has warnings`, `Warnings`, {
              progressBar: true,
              progressAnimation: 'decreasing',
              closeButton: true,
              tapToDismiss: false,
              timeOut: 10 * 1000,
              toastComponent: GeneralToastComponent,
            });
          });
        } else this.warned.clear();
      });
    this.subs.push(sub);
  }

  /**
   * Initiates SquadShot
   */
  async takeSquadshot() {
    this.squadshotService.takeSquadshot();
    this.analyticsService.track('clicked take squadshot');
  }

  /**
   * Setups up Prompts to display chat notification
   */
  async setupUnreadNotification() {
    await this.activeUser$.toPromise();
    const sub = combineLatest([
      this.chatService.getParticipantChatGroups(this.studioSession$.value.sessionID, this.activeUser$.value.uid),
      this.prompts$,
    ]).subscribe(([chatGroups, prompts]) => {
      prompts.forEach((prompt: Prompt) => {
        // only prompt the targets
        if (
          prompt.type === PromptTypes.UNREADCHATGROUP &&
          chatGroups.find((cG) => cG.chatGroupID === prompt?.targetChatGroupID) &&
          prompt.creator !== this.activeUser$.value.uid
        ) {
          this.unreadChatGroups$.next([...this.unreadChatGroups$.value, prompt.targetChatGroupID]);
          this.promptsService.cleanUp(prompt);
        }
      });
    });
    this.subs.push(sub);
  }

  pointerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    clearTimeout(this.lockTimeout);
    clearTimeout(this.editTimeout);
    this.editTimeout = setTimeout(() => {
      this.analyticsService.track('editing studio controls buttons');
      if (!this.editingSidebars$.value) this.editingSidebars$.next(true);
    }, 500);
  }

  pointerUpLeave(e: PointerEvent | MouseEvent) {
    if (e.button !== 0) return;
    clearTimeout(this.editTimeout);
    clearTimeout(this.lockTimeout);
    this.lockTimeout = setTimeout(() => {
      this.editingSidebars$.next(false);
    }, 10000);
  }

  @HostListener('document:click', ['$event'])
  click(event: Event) {
    if (
      this.elRef.nativeElement.contains(event.target as Node) ||
      document.querySelector('sc-add-sidebar')?.contains(event.target as Node)
    ) {
      return;
    }
    this.editingSidebars$.next(false);
  }

  studioButtonTrackBy(index) {
    return index;
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
